/* eslint-disable react/prop-types */
import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { boundingBoxesType, GetResultDataType } from '../../validation/aiocrResult/aiOcrResultType';
import { orange50, orange_overlay30 } from '../../constants/colors';

interface BoundingBoxProps {
  boxes: boundingBoxesType[];
  color: string; // The color of the bounding box
  isFocused: boolean;
  containerWidth: number;
  containerHeight: number;
  imageWidth: number;
  imageHeight: number;
  onFocus?: () => void;
}

interface OverlayProps {
  imageUrl: string;
  result: GetResultDataType;
  imageWidth: number;
  imageHeight: number;
  focusedField: string | null;
  page: number;
  onLoad?: () => void;
}

const BoundingBox: React.FC<BoundingBoxProps> = React.memo(
  ({
    boxes,
    color,
    isFocused,
    containerWidth,
    containerHeight,
    imageWidth,
    imageHeight,
    onFocus,
  }) => {
    const boxRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (isFocused && boxRef.current) {
        boxRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, [isFocused]);

    if (boxes.length < 4) return null;

    const [topLeft, topRight, bottomRight, bottomLeft] = boxes;

    const expandPixels = 3; // 拡大したいピクセル数

    // スケーリング比率を計算
    const scaleX = containerWidth / imageWidth;
    const scaleY = containerHeight / imageHeight;
    const scale = Math.min(scaleX, scaleY);

    // 画像の実際の表示サイズを計算
    const displayWidth = imageWidth * scale;
    const displayHeight = imageHeight * scale;

    // 画像の表示位置のオフセットを計算（中央寄せの場合）
    const offsetX = (containerWidth - displayWidth) / 2;
    const offsetY = (containerHeight - displayHeight) / 2;

    // バウンディングボックスの座標を計算
    const scaledLeft =
      Math.max(0, Math.min(topLeft.x, bottomLeft.x) * scale - expandPixels) + offsetX;
    const scaledTop = Math.max(0, Math.min(topLeft.y, topRight.y) * scale - expandPixels) + offsetY;
    const scaledRight =
      Math.min(displayWidth, Math.max(topRight.x, bottomRight.x) * scale + expandPixels) + offsetX;
    const scaledBottom =
      Math.min(displayHeight, Math.max(bottomLeft.y, bottomRight.y) * scale + expandPixels) +
      offsetY;

    const width = scaledRight - scaledLeft;
    const height = scaledBottom - scaledTop;

    return (
      <div
        ref={boxRef}
        style={{
          position: 'absolute',
          left: `${scaledLeft}px`,
          top: `${scaledTop}px`,
          width: `${width}px`,
          height: `${height}px`,
          border: `1px solid ${color}`,
          backgroundColor: isFocused ? orange_overlay30 : 'transparent',
          boxSizing: 'border-box',
        }}
        onFocus={onFocus}
      />
    );
  },
);
BoundingBox.displayName = 'BoundingBox';

const MemoizedBoundingBox = React.memo(BoundingBox);

const ImageOverlay: React.FC<OverlayProps> = React.memo(
  ({ imageUrl, result, imageWidth, imageHeight, focusedField, page, onLoad }) => {
    const [imageLoaded, setImageLoaded] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

    const memoizedResult = useMemo(
      () => result,
      [
        result.mansionName,
        result.propertyAddress,
        result.roomNumber,
        result.previousOwnerCorporateName,
        result.establishmentDate,
        result.debtorAddress,
        result.debtorName,
        result.mortgageeCompanyName,
        result.loanAmount,
        result.interestRate,
        result.candidateCompanies,
      ],
    );

    const updateContainerSize = (): void => {
      if (containerRef.current) {
        const { offsetWidth, offsetHeight } = containerRef.current;
        setContainerSize(prevSize => {
          if (prevSize.width !== offsetWidth || prevSize.height !== offsetHeight) {
            return { width: offsetWidth, height: offsetHeight };
          }
          return prevSize;
        });
      }
    };

    useEffect(() => {
      window.addEventListener('resize', updateContainerSize);

      return () => {
        window.removeEventListener('resize', updateContainerSize);
      };
    }, []);

    useEffect(() => {
      if (imageLoaded) {
        updateContainerSize();
      }
    }, [imageLoaded]);

    useEffect(() => {
      if (onLoad && imageLoaded) {
        onLoad();
      }
    }, [onLoad, imageLoaded]);

    const renderBoundingBoxes = useCallback(
      (data: GetResultDataType): JSX.Element[] => {
        const boxes: JSX.Element[] = [];

        const singleFields = [
          'mansionName',
          'propertyAddress',
          'roomNumber',
          'previousOwnerCorporateName',
          'establishmentDate',
          'debtorAddress',
          'debtorName',
          'mortgageeCompanyName',
          'loanAmount',
          'interestRate',
          'candidateCompanies',
        ] as const;

        singleFields.forEach(field => {
          if (data[field] && data[field].boundingBoxes && data[field].page === page) {
            boxes.push(
              <MemoizedBoundingBox
                key={field}
                boxes={data[field].boundingBoxes}
                color={orange50}
                isFocused={focusedField === field}
                containerWidth={containerSize.width}
                containerHeight={containerSize.height}
                imageWidth={imageWidth}
                imageHeight={imageHeight}
              />,
            );
          }
        });

        return boxes;
      },
      [containerSize, imageWidth, imageHeight, focusedField, page],
    );

    const handleImageLoad = React.useCallback((): void => {
      if (!imageLoaded) {
        setImageLoaded(true);
        if (onLoad) {
          onLoad();
        }
      }
    }, [imageLoaded, onLoad]);

    return (
      <Box ref={containerRef} position="relative" width="100%">
        <Box
          component="img"
          src={imageUrl}
          alt="Invoice"
          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
          onLoad={handleImageLoad}
        />
        {imageLoaded && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            {renderBoundingBoxes(memoizedResult)}
          </Box>
        )}
      </Box>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.imageUrl === nextProps.imageUrl &&
      JSON.stringify(prevProps.result) === JSON.stringify(nextProps.result) &&
      prevProps.imageWidth === nextProps.imageWidth &&
      prevProps.imageHeight === nextProps.imageHeight &&
      prevProps.focusedField === nextProps.focusedField &&
      prevProps.page === nextProps.page
    );
  },
);
ImageOverlay.displayName = 'ImageOverlay';
export default ImageOverlay;
